import { useEffect, useState } from "react";

interface IntroProps {
  onDone: () => void;
}

export function Intro({ onDone }: IntroProps) {
  const [t, setT] = useState(0);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      onDone();
      return;
    }
    const started = performance.now();
    let raf = 0;
    const loop = (now: number) => {
      setT(now - started);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const leave = window.setTimeout(() => setLeaving(true), 4800);
    const done = window.setTimeout(onDone, 5400);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(leave);
      window.clearTimeout(done);
    };
  }, [onDone]);

  const scene = t < 700 ? 0 : t < 1800 ? 1 : t < 3000 ? 2 : t < 4200 ? 3 : 4;
  const pct = Math.min(100, Math.round((t / 4800) * 100));

  return (
    <div
      className={`fixed inset-0 z-[200] overflow-hidden ${leaving ? "intro-exit" : ""}`}
    >
      {scene === 0 ? (
        <div className="flex h-full items-center justify-center bg-ink">
          <p className="animate-blink font-mono text-xs uppercase tracking-[0.5em] text-acid">
            ■ boot
          </p>
        </div>
      ) : null}

      {scene === 1 ? (
        <div className="flex h-full flex-col items-center justify-center bg-acid text-ink">
          <p className="animate-stamp font-mono text-xs uppercase tracking-[0.4em]">
            Plate 01
          </p>
          <h1 className="animate-slam font-slam text-[clamp(5rem,22vw,14rem)] uppercase leading-none">
            Sonar
          </h1>
        </div>
      ) : null}

      {scene === 2 ? (
        <div className="flex h-full flex-col items-center justify-center bg-tape text-ink">
          <p className="animate-stamp font-mono text-xs uppercase tracking-[0.4em]">
            Plate 02
          </p>
          <h1 className="animate-slam font-slam text-[clamp(5rem,22vw,14rem)] uppercase leading-none">
            Img
          </h1>
        </div>
      ) : null}

      {scene === 3 ? (
        <div className="relative flex h-full flex-col items-center justify-center bg-crate text-cream">
          <div className="hazard absolute inset-x-0 top-0 h-10" />
          <div className="hazard absolute inset-x-0 bottom-0 h-10" />
          <p className="animate-stamp font-mono text-xs uppercase tracking-[0.35em] text-acid">
            Free cloud + local fallback
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {["No card", "No login", "ESRGAN"].map((s, i) => (
              <span
                key={s}
                className="animate-pop border-[3px] border-ink bg-cream px-4 py-2 font-slam text-3xl uppercase text-ink sm:text-5xl"
                style={{ animationDelay: `${i * 80}ms`, rotate: `${(i - 1) * 4}deg` }}
              >
                {s}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {scene === 4 ? (
        <div className="flex h-full flex-col items-center justify-center bg-ink text-acid">
          <p className="animate-slam font-slam text-[clamp(3rem,12vw,8rem)] uppercase leading-none">
            Open the crate
          </p>
          <div className="mt-8 w-[min(80vw,420px)]">
            <div className="h-4 border-[3px] border-acid">
              <div className="h-full bg-acid" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
      ) : null}

      <button
        type="button"
        onClick={onDone}
        className="absolute bottom-6 right-5 z-10 border-[3px] border-current bg-transparent px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-cream mix-blend-difference"
      >
        Skip
      </button>
    </div>
  );
}
